import api from '../services/api';
import { $Qll, $Q } from '../utils/query-selector';
import { dataToggle, toggleDataActive } from '../utils/toggle-dataset';
import {
  updateCartItems,
  updatetotalPrice,
  updateUpsell,
  updateCartbutton,
  updatePriceItem,
  updateCartCounter,
  updateCartHeader,
} from './update-cart';

import { vipLidocainValidation } from './vip-section';
import { closeProductModal } from './product.modal';

import { queryVariants } from './variants-change';

const CART_SECTION = 'side-cart,cart-page';

/**
 *
 * Updates availability badge in product page
 *
 * */

export const handleQuantityBadge = () => {
  const badge = $Q(
    '.main-product__detail .product-disponibility .badge[data-quantity]'
  );
  const target = $Q('.main-product__detail');
  if (!badge || !target) return;

  queryVariants({ target });
};

/**
 * Listen if add to cart form is submited
 * if add to cart form is submited add products in cart
 *
 * @param {string} formQuery - className reference in form add-to-cart
 *
 * To active this feature - ADD className 'add-product-cart' in form product
 * */

// eslint-disable-next-line max-len
export const btnAddToCart = (formQuery) => {
  const addForms = $Qll(formQuery);

  if (addForms != null) {
    addForms.forEach((form) => {
      // eslint-disable-next-line no-use-before-define
      submitForm(form);
    });
  }
};

// eslint-disable-next-line arrow-body-style
export const submitForm = (form) => {
  return form.addEventListener(
    'submit',
    (e) => {
      e.preventDefault();

      // eslint-disable-next-line no-use-before-define
      addProducts(e);

      // eslint-disable-next-line no-unused-expressions
      if(e.target.dataset.form !== 'upsell') {
        dataToggle($Q('#shopify-section-side-cart'), true);
        reInitSideCartActivation();
      }
    },
    true
  );
};

/**
 * Validate if cart was empty and recently added a product to refresh page
 * in order to update the cartpage-footer__content section
 */
const validateIfCartWasEmpty = async () => {
  const page = $Q('#cart-page');
  if (page) {
    // eslint-disable-next-line prefer-template
    const url = window.Shopify.routes.root + 'cart.js';
    const cartContents = await fetch(url)
      .then((response) => response.json())
      .then((data) => data);

    if (cartContents.item_count === 0) {
      return true;
    }
    return false;
  }
  return false;
};

/**
 * Extracts and validates the name pattern for inputs of type:
 * items[0][field][propertyKey?]
 * @param {string} fieldName
 * @returns {{index:number, field:string, propertyKey?:string} | null}
 */
const parseFieldName = (fieldName) => {
  if (!fieldName) return null;

  const match = fieldName.match(/^items\[(\d+)\]\[(.+?)\](?:\[(.+)\])?$/);
  if (!match) return null;

  return {
    index: parseInt(match[1], 10),
    field: match[2],
    propertyKey: match[3],
  };
};

/**
 * Convert to positive integer; if invalid, return fallback.
 * @param {string|number} value
 * @param {number} [fallback=1]
 * @returns {number}
 */
const normalizeQuantity = (value, fallback = 1) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Defines whether an input should be considered for constructing items.
 * Rules:
 *  - checkbox: only if they are marked
 *  - radio: only if they are marked
 *  - others: if they have a value
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input
 * @returns {boolean}
 */
const shouldIncludeInput = (input) => {
  if (!input || input.disabled) return false;

  const type = (input.type || '').toLowerCase();

  if (type === 'checkbox' || type === 'radio') {
    return !!input.checked;
  }

  const val = input.value;
  if (val === null || val === undefined) return false;
  if (typeof val === 'string' && val.trim() === '') return false;

  return true;
};

/**
 * Processes and centralizes the writing of values in the corresponding
 * index for parsed named inputs (items[i][field][propertyKey?]).
 * @param {Record<number, any>} byIndex
 * @param {number} idx
 * @param {string} field - (id | quantity | selling_plan | properties)
 * @param {string|undefined} propertyKey
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input
 */
const processFieldValue = (byIndex, idx, field, propertyKey, input) => {
  if (!byIndex[idx]) byIndex[idx] = {};

  if (!shouldIncludeInput(input)) return;

  switch (field) {
    case 'id':
      byIndex[idx].id = input.value;
      return;
    case 'quantity':
      byIndex[idx].quantity = normalizeQuantity(input.value);
      return;
    case 'selling_plan':
      byIndex[idx].selling_plan = input.value;
      return;
    case 'properties':
      if (!propertyKey) return;
      if (!byIndex[idx].properties) byIndex[idx].properties = {};
      byIndex[idx].properties[propertyKey] = input.value ?? '';
      return;
  }
};

/**
 * Extract the main item from the form.
 * Search: id, quantity-selector, selling-plan-id and properties named "propertie".
 * @param {HTMLFormElement} form
 * @returns {{ id?: string, quantity?: number, selling_plan?: string, properties?: Record<string,string> }}
 *
 * IA: 60%
 */
const extractMainItem = (form) => {
  let mainId = null;
  let mainQty = 1;
  let mainSellingPlan = null;
  const mainProps = {};

  for (const input of form) {
    const name = input?.name;

    if (name === 'id' && shouldIncludeInput(input)) {
      mainId = input.value;
      continue;
    }

    if (name === 'quantity-selector' && shouldIncludeInput(input)) {
      mainQty = normalizeQuantity(input.value, 1);
      continue;
    }

    if (name === 'selling-plan-id' && shouldIncludeInput(input)) {
      mainSellingPlan = input.value;
      continue;
    }

    if (name?.includes('propertie') && shouldIncludeInput(input)) {
      const key = input.dataset?.property;
      if (key) mainProps[key] = input.value ?? '';
    }
  }

  const mainItem = {};
  if (mainId) mainItem.id = mainId;
  if (mainQty) mainItem.quantity = mainQty;
  if (mainSellingPlan) mainItem.selling_plan = mainSellingPlan;
  if (Object.keys(mainProps).length) mainItem.properties = mainProps;

  return mainItem;
};

/**
 * Go through the form and create the “extra” items (bundle, upsell, etc.)
 * defined with the pattern items[i][field][propertyKey?]
 * @param {HTMLFormElement} form
 * @returns {Array<Object>}
 *
 * IA: 60%
 */
const collectExtraItems = (form) => {
  const extraByIndex = {};

  for (const input of form) {
    const parsed = parseFieldName(input?.name);
    if (!parsed) continue;

    const { index, field, propertyKey } = parsed;
    processFieldValue(extraByIndex, index, field, propertyKey, input);
  }

  return Object.keys(extraByIndex)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => extraByIndex[key])
    .filter((item) => item && item.id)
    .map((item) => ({ quantity: 1, ...item }));
};

/**
 * Filter and normalize the array of items for the payload of /cart/add.js
 * - id required
 * - quantity > 0 (or 1 if not specified)
 * @param {Array<Object>} items
 * @returns {Array<Object>}
 */
const buildCleanItems = (items) => {
  return items
    .filter((item) => item && item.id)
    .map((item) => ({
      ...item,
      quantity: normalizeQuantity(item.quantity ?? 1, 1),
    }))
    .filter((item) => item.quantity > 0);
};

/**
 * Create the final payload for /cart/add.js
 * @param {Array<Object>} cleanItems
 * @param {string|string[]} sections
 * @returns {{items:Array<Object>, sections?:string|string[]}}
 */
const buildCartParams = (cleanItems, sections) => {
  const params = { items: cleanItems };
  if (sections) params.sections = sections;
  return params;
};

/**
 * Add products in cart
 * @param {event} event -Event submit from add to cart form
 */
const addProducts = async (event) => {
  event?.preventDefault?.();

  const mainItem = extractMainItem(event.target);
  const extraItems = collectExtraItems(event.target);

  const items = [];
  if (mainItem?.id) items.push(mainItem);
  items.push(...extraItems);

  const cleanItems = buildCleanItems(items);

  if (!cleanItems.length) {
    console.warn('⚠️ No valid items.');
    return;
  }

  const cartParams = buildCartParams(cleanItems, CART_SECTION);

  const isCartEmpty = await validateIfCartWasEmpty();

  const response = await api.addToCart(cartParams);

  if (response) {
    const { sections } = response;
    updateCartItems(sections['side-cart']);
    updatetotalPrice(sections['side-cart']);
    updateUpsell(sections['side-cart']);
    updateCartCounter(sections['side-cart']);
    updateCartHeader(sections['side-cart']);
    closeProductModal(true);
    vipLidocainValidation();

    if (isCartEmpty) {
      const cartFooter = $Q('.cartpage-footer');
      cartFooter?.classList.remove('hidden');
    }
    SufioVAT.onCart();
    initInterceptionSufio();
    handleQuantityBadge();
  }
};

/**
 * Event onChange in the cart item
 */
export const onChangeItemCart = () => {
  $Qll('.item-cart-js').forEach((input) => {
    input.addEventListener(
      'change',
      // eslint-disable-next-line func-names
      function () {
        // eslint-disable-next-line no-use-before-define
        updateCart(this.id, this.value);
      }
    );
  });
};

/**
 * Update quantity for each item in cart
 * @param {number} id Product ID
 * @param {number} quantity new quantity
 */
export const updateCart = async (id, quantity) => {
  const cartParams = {
    id: id,
    quantity: quantity,
    sections: CART_SECTION,
  };

  const { sections, items } = await api.updateCart(cartParams);

  if (quantity === '0') {
    updateCartItems(sections['side-cart']);
    updateUpsell(sections['side-cart']);
    updateCartCounter(sections['side-cart']);
    updateCartHeader(sections['side-cart']);
    updatetotalPrice(sections['side-cart']);
  } else {
    updatePriceItem(sections['cart-page'], id);
    updateCartbutton(sections['side-cart']);
    updatetotalPrice(sections['side-cart']);
    updateCartHeader(sections['side-cart']);
    updateCartCounter(sections['side-cart']);
  }
  vipLidocainValidation();
  if (items.length === 0) {
    const cartFooter = $Q('.cartpage-footer');
    cartFooter?.classList.add('hidden');
  }
  SufioVAT.onCart();
  initInterceptionSufio();
  handleQuantityBadge();
};

/**
 * Delete item in cart
 * listen if delete button is clicked
 * if is clicked, update cart with quantity 0
 */
export const deleteItem = () => {
  const deleteIcon = $Qll('.item-delete');

  if (deleteIcon) {
    deleteIcon.forEach((element) => {
      element.addEventListener('click', () => {
        updateCart(element.dataset.id, '0');
      });
    });
  }
};

export const reInitSideCartActivation = () => {
  toggleDataActive('.cart-close', '#shopify-section-side-cart', {
    overlay: true,
  });
}

/**
 * Open and close side cart with various buttons
 */
export const openCloseCart = () => {
  const cartContainer = $Q('.cart');
  const cartPage = $Q('#cart-page');
  cartContainer.setAttribute('data-active', 'false');

  if (!cartPage) {
    reInitSideCartActivation()

    toggleDataActive('.button-cart', '#shopify-section-side-cart', {
      overlay: true,
    });
  }
};

/**
 * Show or hide scroll DOM when side cart is open
 * @param {Node HTML} node
 */
export const showOrHideScrollHtml = (node) => {
  node.dataset.active === 'true'
    ? document.getElementsByTagName('html')[0].classList.add('hidden-scroll')
    : document
        .getElementsByTagName('html')[0]
        .classList.remove('hidden-scroll');
};

/**
 * Show or hide widgets when side cart is opened
 */
export const hideOrShowWidgets = () => {
  const cartContainer = $Q('.cart');
  const sideBarMenu = $Q('.sidebar-menu');
  const richPanel = $Q('.richpanel-micro');
  const axeptio = $Q('#axeptio_overlay');

  if (
    cartContainer.dataset.active === 'true' ||
    sideBarMenu.dataset.active === 'true'
  ) {
    richPanel?.classList.add('hidden');
    axeptio?.classList.add('hidden');
  } else {
    richPanel?.classList.remove('hidden');
    axeptio?.classList.remove('hidden');
  }
};

/**
 * The function `updateSufio` adds a VAT label and button to a form if the customer object is empty or
 * if the customer VAT is empty.
 * @param sufioForm - The `sufioForm` parameter is the form element that is being updated. It is used
 * to select and manipulate elements within the form.
 */
const updateSufio = (sufioForm) => {
  const btnVat = $Q('.sufio-vat-link', sufioForm);
  const labelVat = document.createElement('span');
  sufioForm.classList.add('sufio-vat-form-js');
  labelVat.setAttribute('class', 'sufio-vat-label');
  labelVat.innerHTML = sufioLabel;
  sufioForm.insertBefore(labelVat, btnVat);

  btnVat.addEventListener('click', () => {
    labelVat.classList.add('hidden');
  });

  $Q('.sufio-vat-cancel-button', sufioForm).addEventListener('click', () => {
    labelVat.classList.remove('hidden');
  });
};

/**
 * The function `initInterceptionSufio` initializes the interception of Sufio forms on page load.
 */
export const initInterceptionSufio = () => {
  // const lstSufioForms = $Qll('.sufio-vat-form');
  // lstSufioForms.forEach((sufioForm) => updateSufio(sufioForm));

  const headerSuifioVat = $Q('.header-sufio-vat');
  if (!headerSuifioVat) return;

  headerSuifioVat.addEventListener('click', (e) => {
    const status = e.target.dataset.active === 'true';

    e.target.dataset.active = !status;
  });
};
